import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, Label, Input } from 'reactstrap';
import _ from 'underscore';
import Icon from '../containers/icon';

/**
 *  Provide search to table views.
 *
 *  This needs to be a controlled component that keeps its
 *  state updated with the latest props, which is necessary
 *  so that search input can be buffered (i.e., debounced).
 *  Found a great React debouncing thread at
 *  http://stackoverflow.com/questions/23123138/perform-debounce-in-react-js.
 */
const SearchInput = _ref => {
  let _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    onChange = _ref.onChange,
    onClear = _ref.onClear,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'lg' : _ref$size,
    _ref$text = _ref.text,
    text = _ref$text === void 0 ? null : _ref$text,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? 'Search' : _ref$placeholder;
  const _useState = useState(text || ''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];

  // Use a ref to always have access to the latest onChange callback.
  // This allows the debounce function to remain stable while still calling
  // the most up-to-date callback when it fires.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Create the debounce function only once (on mount).
  // It calls through onChangeRef to always use the latest onChange callback.
  // This prevents the debounce from being cancelled/recreated when the parent
  // component re-renders and passes a new onChange function reference.
  const delayedQuery = useMemo(() => _.debounce(q => onChangeRef.current(q), 500), [] // Empty deps - created only once
  );

  // Cleanup: cancel debounce on unmount
  useEffect(() => () => {
    delayedQuery.cancel();
  }, [delayedQuery]);

  // refs are state variables that don't cause rerender. This is useful to compare the
  // props causing rerender against the last search query.
  const lastQuery = useRef();
  useEffect(() => {
    // when text prop is set to '' or null, clear the search.
    if (lastQuery.current && (text === '' || text === null)) {
      setQuery('');
      onChangeRef.current('');
    } else if (text !== null && text !== void 0 && text.length && text !== query && query === lastQuery.current) {
      // if there's incoming text and it doesn't match the query, but the query
      // matches the reference lastQuery a cabinet switch has occurred and the UI should
      // reset the search value
      setQuery(text);
    }
    lastQuery.current = text;
  }, [query, text]);
  const handleSearch = e => {
    setQuery(e.target.value);
    delayedQuery(e.target.value);
  };
  const renderActionIcon = () => {
    if (loading) {
      return /*#__PURE__*/React.createElement("span", {
        className: "input-spinner t-si-loading",
        "data-testid": "t-si-loading"
      }, /*#__PURE__*/React.createElement(Icon, {
        glyph: "spinner",
        className: "fa-spin"
      }));
    }
    if (text) {
      return /*#__PURE__*/React.createElement("span", {
        "aria-label": "clear-search-field",
        "data-testid": "t-si-clear",
        role: "button",
        tabIndex: 0,
        className: "clearMe t-si-clear",
        onKeyDown: onClear,
        onClick: onClear
      }, /*#__PURE__*/React.createElement(Icon, {
        glyph: "times-circle"
      }));
    }
    return null;
  };
  return /*#__PURE__*/React.createElement("form", {
    className: "search"
  }, /*#__PURE__*/React.createElement(FormGroup, null, label ? /*#__PURE__*/React.createElement(Label, null, label) : null, /*#__PURE__*/React.createElement("div", {
    className: "input-group mb-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group-prepend"
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group-text"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "search"
  }))), /*#__PURE__*/React.createElement(Input, {
    "data-testid": "t-si-input",
    id: "search",
    type: "text",
    value: query,
    placeholder: placeholder,
    bsSize: size,
    onChange: handleSearch,
    className: "t-si-input ".concat(className, " tqc-control"),
    onKeyDown: event => {
      if (event.keyCode === 13) {
        event.stopPropagation();
        event.preventDefault();
        onChangeRef.current(event.target.value);
      }
    }
  }), renderActionIcon())));
};
SearchInput.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  size: PropTypes.string,
  label: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  text: PropTypes.string
};
export default SearchInput;